import React, { useState, useEffect, useCallback } from 'react';
import {
  Typography,
  Card,
  Form,
  Input,
  InputNumber,
  Button,
  Table,
  Space,
  Modal,
  message,
  Select,
  Tag,
  Switch,
  Popconfirm
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SyncOutlined,
  AppstoreOutlined
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useAdmin } from '../../hooks/useAdmin';

const { Title, Text } = Typography;
const { Option } = Select;

const InvestmentPlans = () => {
  const { t } = useTranslation();
  const { 
    getInvestmentPlans, 
    createInvestmentPlan, 
    updateInvestmentPlan, 
    deleteInvestmentPlan,
    updatePlanStatus,
    getInvestmentTypes
  } = useAdmin();
  
  const [plans, setPlans] = useState([]);
  const [types, setTypes] = useState([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [plansRes, typesRes] = await Promise.all([
        getInvestmentPlans(),
        getInvestmentTypes()
      ]);
      
      if (plansRes.success) setPlans(plansRes.data);
      if (typesRes.success) setTypes(typesRes.data);
    } catch (error) {
      message.error(t('admin.investment_plans.messages.load_error'));
    } finally {
      setLoading(false);
    }
  }, [getInvestmentPlans, getInvestmentTypes, t]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleAddPlan = () => {
    setEditingPlan(null);
    form.resetFields();
    form.setFieldsValue({
      is_active: true,
      min_amount: 100,
      max_amount: 10000,
      duration_days: 30,
      daily_return_percent: 1,
      asset: 'USDT',
      daily_mining_rate: 0,
      mining_asset: 'ENV'
    });
    setModalVisible(true);
  };

  const handleEditPlan = (plan) => {
    setEditingPlan(plan);
    form.setFieldsValue(plan);
    setModalVisible(true);
  };

  const handleSavePlan = async () => {
    try {
      const values = await form.validateFields();
      let result;

      if (editingPlan) {
        result = await updateInvestmentPlan(editingPlan.id, values);
      } else {
        result = await createInvestmentPlan(values);
      }

      if (result.success) {
        message.success(editingPlan ? t('admin.investment_plans.messages.update_success') : t('admin.investment_plans.messages.create_success'));
        setModalVisible(false);
        loadData();
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleDeletePlan = async (id) => {
    const result = await deleteInvestmentPlan(id);
    if (result.success) {
      message.success(t('admin.investment_plans.messages.delete_success'));
      loadData();
    }
  };

  const handleToggleStatus = async (id, status) => {
    const result = await updatePlanStatus(id, status);
    if (result.success) {
      message.success(t('admin.investment_plans.messages.status_success'));
      loadData();
    }
  };

  const columns = [
    {
      title: t('admin.investment_plans.table.name'),
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <Space direction="vertical" size={0}>
          <Text strong>{text}</Text>
          {record.type_name && <Tag color="purple" style={{ fontSize: '10px' }}>{record.type_name}</Tag>}
        </Space>
      ),
    },
    {
      title: t('admin.investment_plans.table.type'),
      dataIndex: 'type_name',
      key: 'type_name',
      render: (name) => name || <Text type="secondary">N/A</Text>
    },
    {
      title: t('admin.investment_plans.table.amounts'),
      key: 'amounts',
      render: (_, record) => (
        <Text small>Min: ${record.min_amount} / Max: ${record.max_amount || '∞'}</Text>
      )
    },
    {
      title: t('admin.investment_plans.table.duration'),
      dataIndex: 'duration_days',
      key: 'duration_days',
      render: (days) => `${days} ${t('admin.investment_plans.table.days')}`,
    },
    {
      title: t('admin.investment_plans.table.return'),
      key: 'returns',
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Text>{record.daily_return_percent}% / {t('admin.investment_plans.table.per_day')}</Text>
          <Text type="secondary" style={{ fontSize: '11px' }}>{t('admin.investment_plans.table.total')}: {(record.daily_return_percent * record.duration_days).toFixed(1)}%</Text>
        </Space>
      )
    },
    {
      title: 'Mining Bonus',
      key: 'mining',
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          {parseFloat(record.daily_mining_rate) > 0 ? (
            <Tag color="gold">+{record.daily_mining_rate} {record.mining_asset}/day</Tag>
          ) : (
            <Text type="secondary">-</Text>
          )}
        </Space>
      )
    },
    {
      title: t('admin.investment_plans.table.status'),
      dataIndex: 'is_active',
      key: 'is_active',
      render: (isActive, record) => (
        <Space>
          <Tag color={isActive ? 'green' : 'red'}>
            {isActive ? t('admin.investment_plans.table.active') : t('admin.investment_plans.table.inactive')}
          </Tag>
          <Switch
            checked={isActive}
            onChange={(checked) => handleToggleStatus(record.id, checked)}
            size="small"
          />
        </Space>
      ),
    },
    {
      title: t('admin.investment_plans.table.actions'),
      key: 'actions',
      width: 120,
      render: (_, record) => (
        <Space>
          <Button type="text" icon={<EditOutlined />} onClick={() => handleEditPlan(record)} style={{ color: '#6366f1' }} />
          <Popconfirm title={t('admin.investment_plans.messages.delete_confirm')} onConfirm={() => handleDeletePlan(record.id)}>
            <Button type="text" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="admin-plans-inner">
      <div className="page-title-section" style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <Title level={2} style={{ margin: 0, color: '#f1f5f9' }}>
            <AppstoreOutlined style={{ marginRight: 12, color: '#6366f1' }} />
            {t('admin.investment_plans.title')}
          </Title>
          <Text type="secondary">{t('admin.investment_plans.subtitle')}</Text>
        </div>
        <Space>
          <Button icon={<SyncOutlined spin={loading} />} onClick={loadData}>{t('admin.investment_plans.refresh')}</Button>
          <Button 
            type="primary" 
            icon={<PlusOutlined />} 
            onClick={handleAddPlan}
            style={{ borderRadius: 8, height: 40, background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)', border: 'none' }}
          >
            {t('admin.investment_plans.add_plan')}
          </Button>
        </Space>
      </div>

      <Card className="glass-card" style={{ background: 'rgba(30, 41, 59, 0.4)', backdropFilter: 'blur(8px)', border: '1px solid rgba(255,255,255,0.05)' }}>
        <Table 
          columns={columns} 
          dataSource={plans} 
          rowKey="id"
          loading={loading}
          className="admin-table"
        />
      </Card>

      <Modal
        title={editingPlan ? t('admin.investment_plans.form.edit_title') : t('admin.investment_plans.form.new_title')}
        open={modalVisible}
        onOk={handleSavePlan}
        onCancel={() => setModalVisible(false)}
        width={700}
        centered
      >
        <Form form={form} layout="vertical">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <Form.Item name="name" label={t('admin.investment_plans.form.name_label')} rules={[{ required: true }]}>
              <Input />
            </Form.Item>
            <Form.Item name="type_id" label={t('admin.investment_plans.form.type_label')}>
              <Select placeholder={t('admin.investment_plans.form.type_placeholder')}>
                {types.map(t => <Option key={t.id} value={t.id}>{t.name}</Option>)}
              </Select>
            </Form.Item>
          </div>
          
          <Form.Item name="description" label={t('admin.investment_plans.form.description')}>
            <Input.TextArea rows={2} />
          </Form.Item>
          
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>
            <Form.Item name="min_amount" label={t('admin.investment_plans.form.min_amount')} rules={[{ required: true }]}>
              <InputNumber style={{ width: '100%' }} prefix="$" />
            </Form.Item>
            <Form.Item name="max_amount" label={t('admin.investment_plans.form.max_amount')} rules={[{ required: true }]}>
              <InputNumber style={{ width: '100%' }} prefix="$" />
            </Form.Item>
            <Form.Item name="asset" label={t('admin.investment_plans.form.asset')} initialValue="USDT">
              <Select>
                <Option value="USDT">USDT</Option>
                <Option value="BTC">BTC</Option>
                <Option value="ETH">ETH</Option>
              </Select>
            </Form.Item>
          </div>
          
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <Form.Item name="duration_days" label={t('admin.investment_plans.form.duration_label')} rules={[{ required: true }]}>
              <InputNumber style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item name="daily_return_percent" label={t('admin.investment_plans.form.daily_return')} rules={[{ required: true }]}>
              <InputNumber style={{ width: '100%' }} addonAfter="%" />
            </Form.Item>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <Form.Item name="daily_mining_rate" label="Daily Mining Rate (Internal Crypto)" initialValue={0}>
              <InputNumber style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item name="mining_asset" label="Mining Asset Symbol" initialValue="ENV">
              <Input />
            </Form.Item>
          </div>
          
          <Form.Item name="is_active" label={t('admin.investment_plans.form.status')} valuePropName="checked" initialValue={true}>
            <Switch checkedChildren={t('admin.investment_plans.table.active')} unCheckedChildren={t('admin.investment_plans.table.inactive')} />
          </Form.Item>
        </Form>
      </Modal>

      <style dangerouslySetInnerHTML={{ __html: `
        .admin-page-container { animation: fadeIn 0.5s ease-out; }
        .glass-card { box-shadow: 0 8px 32px 0 rgba(0, 0, 0, 0.37); }
        .admin-table .ant-table { background: transparent !important; }
        .admin-table .ant-table-thead > tr > th { background: rgba(15, 23, 42, 0.8) !important; color: #94a3b8 !important; border-bottom: 1px solid rgba(255,255,255,0.05) !important; }
        .admin-table .ant-table-tbody > tr > td { border-bottom: 1px solid rgba(255,255,255,0.02) !important; }
        .admin-table .ant-table-tbody > tr:hover > td { background: rgba(99, 102, 241, 0.05) !important; }
        @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
      `}} />
    </div>
  );
};


export default InvestmentPlans;